import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../state/useGameStore';
import { useChat } from '../hooks/useChat';
import type { ChatMessage } from '../shared/protocol';

type Props = {
  sendMessage: (msg: unknown) => void;
};

function teamColor(team: ChatMessage['team']) {
  if (team === 'storm') return '#70d6ff';
  if (team === 'blood_eagle') return '#ff7d7d';
  return '#d0e8ff';
}

export function ChatOverlay({ sendMessage }: Props) {
  const rawMessages = useGameStore((s) => s.chatMessages);
  const chatMessages = rawMessages ?? [];
  const { chatOpen, setChatOpen, sendChat } = useChat(sendMessage);
  const [inputText, setInputText] = useState('');
  const [chatTeam, setChatTeam] = useState<'all' | 'team'>('all');
  const [fadeOut, setFadeOut] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-hide messages after 4s when chat is closed
  useEffect(() => {
    if (!chatOpen) {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = setTimeout(() => setFadeOut(true), 4000);
    } else {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      setFadeOut(false);
    }
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [chatOpen]);

  // Reset fade when new messages arrive
  useEffect(() => {
    if (chatMessages.length > 0) {
      setFadeOut(false);
      if (!chatOpen) {
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = setTimeout(() => setFadeOut(true), 4000);
      }
    }
  }, [chatMessages.length, chatOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (chatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chatOpen]);

  // Key bindings
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if typing in another input
      const target = e.target as HTMLElement;
      if (target !== document.body && target !== inputRef.current && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }

      if (e.key === 'Enter') {
        if (chatOpen) {
          // Submit if there's text, then close
          if (inputText.trim()) {
            sendChat(inputText, chatTeam);
            setInputText('');
          }
          setChatOpen(false);
        } else {
          setChatTeam('all');
          setChatOpen(true);
          e.preventDefault();
        }
        return;
      }

      if (e.key === 'Escape' && chatOpen) {
        setInputText('');
        setChatOpen(false);
        return;
      }

      if ((e.key === 't' || e.key === 'T') && !chatOpen) {
        e.preventDefault();
        setChatTeam('all');
        setChatOpen(true);
        return;
      }

      if ((e.key === 'y' || e.key === 'Y') && !chatOpen) {
        e.preventDefault();
        setChatTeam('team');
        setChatOpen(true);
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chatOpen, inputText, chatTeam, sendChat, setChatOpen]);

  const visibleMessages = chatMessages.slice(-8);
  const msgOpacity = chatOpen ? 1 : fadeOut ? 0 : 0.7;
  const transition = 'opacity 0.5s ease';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 120,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 400,
        zIndex: 150,
        pointerEvents: chatOpen ? 'auto' : 'none',
        userSelect: chatOpen ? 'auto' : 'none',
      }}
    >
      {/* Message history */}
      <div
        style={{
          opacity: msgOpacity,
          transition,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          marginBottom: chatOpen ? 4 : 0,
        }}
      >
        {visibleMessages.map((msg) => (
          <div
            key={msg.id}
            style={{
              background: 'rgba(0,0,0,0.5)',
              padding: '4px 8px',
              borderRadius: 4,
              marginBottom: 2,
              fontSize: 12,
              lineHeight: 1.4,
              color: '#edf7ff',
              fontFamily: 'monospace',
            }}
          >
            <span style={{ color: teamColor(msg.team), fontWeight: 700 }}>[{msg.playerName}]</span>{' '}
            {msg.text}
          </div>
        ))}
      </div>

      {/* Input area */}
      {chatOpen && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'rgba(5,10,20,0.72)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(80,180,255,0.4)',
            borderRadius: 4,
            padding: '4px 8px',
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: chatTeam === 'team' ? '#ffcc66' : '#50b4ff',
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
            }}
          >
            {chatTeam === 'team' ? 'TEAM' : 'ALL'}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            maxLength={120}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                if (inputText.trim()) {
                  sendChat(inputText, chatTeam);
                  setInputText('');
                }
                setChatOpen(false);
              } else if (e.key === 'Escape') {
                setInputText('');
                setChatOpen(false);
              }
            }}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#edf7ff',
              fontSize: 12,
              fontFamily: 'monospace',
              caretColor: '#50b4ff',
            }}
            placeholder="Type message…"
          />
          <span style={{ fontSize: 9, color: 'rgba(200,220,240,0.45)', whiteSpace: 'nowrap' }}>
            Enter to send
          </span>
        </div>
      )}
    </div>
  );
}
